import { Page, withModel } from '@adobe/aem-react-editable-components';
import React from 'react';
import { ModalProvider } from './providers/Modal/Modal';
import { SpeedbumpProvider } from './providers/Speedbump/Speedbump';
import { WidthContextProvider } from './providers/Width';
import { PushdownProvider } from './providers/Pushdown/PushdownProvider';
import { SideMenuProvider } from './providers/SideMenuProvider/SideMenuProvider';
import { TermRatesProvider } from './providers/TermRates/TermRates';
import { AcquisitionContextProvider } from './providers/AcquisitionProvider/AcquisitionProvider';
import { MitekContextProvider } from './providers/MitekProvider/MitekProvider';
import FeatureFlagProvider from './providers/FeatureFlagProvider/FeatureFlagProvider';
import { MessageWrapper } from './components/MessageWrapper/MessageWrapper';
import { SignUpContextProvider } from './components/SignUp/provider/SignUpProvider';
import SessionStorage from './utils/SessionStorage';
import SessionStorageStatusProvider from './providers/SessionStorageStatus/SessionStorageStatusProvider';
import LoaderProvider from './providers/Loader/LoaderProvider';
import WhitelistProvider from './providers/WhitelistProvider/WhitelistProvider';
import { ApiTokenProvider } from './providers/ApiTokenProvider/ApiTokenProvider';
import { UnsupportedBrowsersWrapper } from './components/UnsupportedBrowsersWrapper/UnsupportedBrowsersWrapper';
import PersonalizationCustomizationContextProvider from './providers/PersonalizationCustomizationProvider/PersonalizationCustomizationProvider';
import { flowDataModule } from './utils/flowDataModule';
import { clearSessionStorageVariablesForAcquisitionFlow } from './utils/acquisition-utils';
import AllProvider from './providers/AllProvider';
import { ModernAcquisitionProvider } from './providers/ModernAcquisitionProvider/ModernAcquisitionProvider';

/**
 * Used to capture the DF3 query param containing a unique marketing campaign code.
 * If it exists it will save in session storage using MarketingAttributes object.
 * This is part of an initiative to track marketing campaigns DX-11576
 */
function captureDF3Param() {
    const params = new URLSearchParams(window.location.search);
    const marketingAttributes = SessionStorage.tryGetObject(
        'MarketingAttributes',
        {},
    );

    if (params.has('DF3')) {
        Object.assign(marketingAttributes, { DF3: params.get('DF3') });
    }

    if (params.has('df3')) {
        Object.assign(marketingAttributes, { DF3: params.get('df3') });
    }

    SessionStorage.setObject('MarketingAttributes', marketingAttributes);
}

// This component is the application entry point

class App extends Page {
    componentDidMount() {
        const { hash } = window.location;

        if (hash) {
            let cancelled = false;
            const cancel = () => {
                cancelled = true;
                window.removeEventListener('wheel', cancel);
                window.removeEventListener('touchmove', cancel);
                window.removeEventListener('load', onLoad);
            };
            const scrollToHash = (behavior) => {
                if (cancelled) return;
                const el = document.getElementById(hash.slice(1));
                if (el) el.scrollIntoView({ behavior });
            };
            const onLoad = () => scrollToHash('smooth');

            scrollToHash('smooth');
            window.addEventListener('load', onLoad);
            window.addEventListener('wheel', cancel, { passive: true });
            window.addEventListener('touchmove', cancel, { passive: true });
        }

        const insideIframe = window.self !== window.top;
        if (!this.props.includeAcquisitionProvider && !insideIframe) {
            clearSessionStorageVariablesForAcquisitionFlow();
        }

        captureDF3Param();

        if (window.top === window.self) {
            SessionStorage.removeOutOfScopeItems();
            flowDataModule.getFromSession().then(flowData => {
                window.digitalData.analyticsData.flowData = flowData;
            });
        }
    }

    render() {
        if (
            !JSON.parse(
                document.querySelector('meta[property="featureFlagsProvider"]')
                    ?.content || '{}',
            )['DPM-12131-Provider-Composition']
        ) {
            return this.props.includeAcquisitionProvider ? (
                <FeatureFlagProvider>
                    <WidthContextProvider>
                        <ModalProvider>
                            <PushdownProvider>
                                <SpeedbumpProvider>
                                    <TermRatesProvider>
                                        <ApiTokenProvider>
                                            <ModernAcquisitionProvider>
                                                <AcquisitionContextProvider>
                                                    <PersonalizationCustomizationContextProvider>
                                                        <MitekContextProvider>
                                                            <WhitelistProvider>
                                                                <SessionStorageStatusProvider>
                                                                    <LoaderProvider>
                                                                        <SignUpContextProvider>
                                                                            <div className='aem-page-container'>
                                                                                <SideMenuProvider>
                                                                                    <MessageWrapper>
                                                                                        <UnsupportedBrowsersWrapper>
                                                                                            {
                                                                                                this
                                                                                                    .childComponents
                                                                                            }
                                                                                            {
                                                                                                this
                                                                                                    .childPages
                                                                                            }
                                                                                        </UnsupportedBrowsersWrapper>
                                                                                    </MessageWrapper>
                                                                                </SideMenuProvider>
                                                                            </div>
                                                                        </SignUpContextProvider>
                                                                    </LoaderProvider>
                                                                </SessionStorageStatusProvider>
                                                            </WhitelistProvider>
                                                        </MitekContextProvider>
                                                    </PersonalizationCustomizationContextProvider>
                                                </AcquisitionContextProvider>
                                            </ModernAcquisitionProvider>
                                        </ApiTokenProvider>
                                    </TermRatesProvider>
                                </SpeedbumpProvider>
                            </PushdownProvider>
                        </ModalProvider>
                    </WidthContextProvider>
                </FeatureFlagProvider>
            ) : (
                <FeatureFlagProvider>
                    <WidthContextProvider>
                        <ModalProvider>
                            <PushdownProvider>
                                <SpeedbumpProvider>
                                    <TermRatesProvider>
                                        <ApiTokenProvider>
                                            <WhitelistProvider>
                                                <SessionStorageStatusProvider>
                                                    <LoaderProvider>
                                                        <SignUpContextProvider>
                                                            <div className='aem-page-container'>
                                                                <SideMenuProvider>
                                                                    <MessageWrapper>
                                                                        <UnsupportedBrowsersWrapper>
                                                                            {
                                                                                this
                                                                                    .childComponents
                                                                            }
                                                                            {
                                                                                this
                                                                                    .childPages
                                                                            }
                                                                        </UnsupportedBrowsersWrapper>
                                                                    </MessageWrapper>
                                                                </SideMenuProvider>
                                                            </div>
                                                        </SignUpContextProvider>
                                                    </LoaderProvider>
                                                </SessionStorageStatusProvider>
                                            </WhitelistProvider>
                                        </ApiTokenProvider>
                                    </TermRatesProvider>
                                </SpeedbumpProvider>
                            </PushdownProvider>
                        </ModalProvider>
                    </WidthContextProvider>
                </FeatureFlagProvider>
            );
        }

        return (
            <AllProvider
                includeAcquisitionProvider={
                    this.props.includeAcquisitionProvider
                }
            >
                {this.childComponents}
                {this.childPages}
            </AllProvider>
        );
    }
}

export default withModel(App);
